
import React from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import CoursePlayer from './pages/CoursePlayer';
import ManagerDashboard from './pages/ManagerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Subscription from './pages/Subscription';
import SetupAccount from './pages/SetupAccount';
import Resources from './pages/Resources';
import Forum from './pages/Forum';
import Broadcast from './pages/Broadcast';
import PaymentSuccess from './pages/PaymentSuccess';
import DirectMessages from './pages/DirectMessages';
import Settings from './pages/Settings';
import InstallPrompt from './components/InstallPrompt';

import Home from './pages/Home';
import BuddyMatcher from './pages/BuddyMatcher';
import LiPSandbox from './pages/LiPSandbox';
import ForceNameUpdate from './components/ForceNameUpdate';

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  return currentUser ? <>{children}</> : <Navigate to="/" />;
};

const ManagerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile, schoolProfile, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  
  if (!currentUser) return <Navigate to="/" />;
  
  const isOrgManager = userProfile?.role === 'manager' && schoolProfile?.planTier === 'tier_org';
  const isAdmin = userProfile?.role === 'admin';
  const isDadsMatterTeam = userProfile?.schoolId === 'dadsmatter';

  return (isOrgManager || isAdmin || isDadsMatterTeam) ? <>{children}</> : <Navigate to="/home" />;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  
  if (!currentUser) return <Navigate to="/" />;
  // Strictly role-based admin check
  return userProfile?.role === 'admin' ? <>{children}</> : <Navigate to="/home" />;
};

const SubscriptionGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { userProfile, schoolProfile, loading } = useAuth();
    const location = useLocation();

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    
    // Admins and Dads Matter Team members always have access
    const isPro = userProfile?.role === 'admin' || userProfile?.schoolId === 'dadsmatter';
    if (isPro) return <>{children}</>;

    // Support both User-level and School-level payment checks
    const userIsPaid = userProfile?.isPaid === true || userProfile?.subscriptionStatus === 'active';
    const schoolIsPaid = schoolProfile?.isPaid === true || schoolProfile?.subscriptionStatus === 'active';

    if (!userIsPaid && !schoolIsPaid) {
        return <Navigate to={`/subscription${location.search}`} replace />;
    }
    
    return <>{children}</>;
};

// Removed SandboxGuard entirely because the paywall is now handled natively inside LiPSandbox.tsx

function App() {
  return (
    <AuthProvider>
      <Router>
        <ForceNameUpdate>
          <InstallPrompt />
          <Routes>
            <Route path="/" element={<Auth />} />
            <Route path="/setup" element={<SetupAccount />} />
          
          <Route path="/subscription" element={
            <PrivateRoute>
               <Subscription />
            </PrivateRoute>
          } />

          <Route path="/payment-success" element={
            <PrivateRoute>
                <PaymentSuccess />
            </PrivateRoute>
          } />

          <Route path="/home" element={
            <PrivateRoute>
                <Home />
            </PrivateRoute>
          } />

          <Route path="/dashboard" element={
            <PrivateRoute>
                <SubscriptionGuard>
                    <Dashboard />
                </SubscriptionGuard>
            </PrivateRoute>
          } />

          <Route path="/resources" element={
            <PrivateRoute>
                <SubscriptionGuard>
                    <Resources />
                </SubscriptionGuard>
            </PrivateRoute>
          } />

          <Route path="/hub" element={
            <PrivateRoute>
                <Forum />
            </PrivateRoute>
          } />

          <Route path="/messages" element={
            <PrivateRoute>
                <SubscriptionGuard>
                    <DirectMessages />
                </SubscriptionGuard>
            </PrivateRoute>
          } />

          <Route path="/settings" element={
            <PrivateRoute>
               <Settings />
            </PrivateRoute>
          } />

          {/* Broadcast is public so speakers can join via token, internal logic handles permissions */}
          <Route path="/broadcast" element={<Broadcast />} />
          
          <Route path="/course/:courseId" element={
            <PrivateRoute>
                <SubscriptionGuard>
                    <CoursePlayer />
                </SubscriptionGuard>
            </PrivateRoute>
          } />

          <Route path="/sandbox" element={
            <PrivateRoute>
                <SubscriptionGuard>
                    <LiPSandbox />
                </SubscriptionGuard>
            </PrivateRoute>
          } />

          <Route path="/buddy-matcher" element={
            <PrivateRoute>
                <SubscriptionGuard>
                    <BuddyMatcher />
                </SubscriptionGuard>
            </PrivateRoute>
          } />
          
          <Route path="/manager" element={
            <ManagerRoute>
                <SubscriptionGuard>
                    <ManagerDashboard />
                </SubscriptionGuard>
            </ManagerRoute>
          } />

            <Route path="/admin" element={
              <AdminRoute>
                  <AdminDashboard />
              </AdminRoute>
            } />
          </Routes>
        </ForceNameUpdate>
      </Router>
    </AuthProvider>
  );
}

export default App;
